const SIZE_PATTERN_CACHE = new Map()

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * Matches a size value as a whole token inside an alt string, so "S" does not
 * match "Sandals" and "XL" does not match "XLarge Print".
 */
const sizePattern = (size) => {
    const key = size.toLowerCase()
    if (!SIZE_PATTERN_CACHE.has(key)) {
        SIZE_PATTERN_CACHE.set(
            key,
            new RegExp(
                `(^|[^\\p{L}\\p{N}])${escapeRegExp(key)}([^\\p{L}\\p{N}]|$)`,
                "iu"
            )
        )
    }
    return SIZE_PATTERN_CACHE.get(key)
}

class ProductGallery extends HTMLElement {
    constructor() {
        super()
        this.observeSelectedVariantAttribute()
    }

    connectedCallback() {
        this.elements = {
            prevButton: this.querySelector(".prev-button"),
            nextButton: this.querySelector(".next-button"),
            progressBar: this.querySelector("[data-progress-bar]"),
            progressContainer: this.querySelector(".gallery-progress"),
        }

        this.options = {
            axis: "x",
            container: "[data-track]",
            slides: "[data-slide]",
            loop: false,
        }

        this.option_thumbs = {
            containScroll: "keepSnaps",
            dragFree: true,
        }

        this.viewportNodeMainCarousel = this.querySelector(".main-viewport")
        this.viewportNodeThumbCarousel = this.querySelector(
            ".thumbnail-viewport"
        )

        // Full, unfiltered set of nodes. Slides are detached from / reattached
        // to their track as the selected size changes, so we keep the originals
        // (and their original order) here.
        this.mainTrack = this.viewportNodeMainCarousel.querySelector(
            "[data-track]"
        )
        this.thumbTrack = this.viewportNodeThumbCarousel
            ? this.viewportNodeThumbCarousel.querySelector("[data-track]")
            : null
        this.mainSlides = [...this.mainTrack.children]
        this.thumbSlides = this.thumbTrack ? [...this.thumbTrack.children] : []
        this.desktopSlides = [...this.querySelectorAll("[data-desktop-media]")]
        this.variantSizeMap = this.readVariantSizeMap()

        // Filter before Embla measures the track, otherwise snap points are
        // calculated against slides that are about to be removed.
        this.applyVariantFilter(this.getSelectedVariantId(), false)

        this.init()
        this.handleInitialVariantSelection()
    }

    readVariantSizeMap() {
        const node = this.querySelector("[data-variant-size-map]")
        if (!node) return null

        try {
            return JSON.parse(node.textContent)
        } catch (error) {
            console.warn("Product gallery: invalid variant size map.", error)
            return null
        }
    }

    getSelectedVariantId() {
        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        if (!closestElement) return null
        return closestElement.getAttribute(
            "data-product-card-selected_variant_id"
        )
    }

    getSizeForVariant(variantId) {
        if (!variantId || !this.variantSizeMap) return null
        const size = this.variantSizeMap[String(variantId)]
        if (typeof size !== "string" || !size.trim()) return null
        return size.trim()
    }

    slideMatchesSize(node, size) {
        const alt = node.dataset.alt || ""
        if (!alt) return false
        return sizePattern(size).test(alt)
    }

    /**
     * Keeps only the media whose alt text contains the selected variant's size.
     * With no variant selected, no size option, or no matching alt text at all,
     * the full set is shown.
     */
    applyVariantFilter(variantId, reinit = true) {
        const size = this.getSizeForVariant(variantId)
        const allIndexes = this.mainSlides.map((_, index) => index)

        let visibleIndexes = allIndexes
        if (size) {
            const matched = allIndexes.filter((index) =>
                this.slideMatchesSize(this.mainSlides[index], size)
            )
            // Never leave the gallery empty — fall back to every slide.
            if (matched.length) visibleIndexes = matched
        }

        const key = visibleIndexes.join(",")
        if (key === this.visibleKey) return
        this.visibleKey = key

        const visible = new Set(visibleIndexes)

        this.syncTrack(this.mainTrack, this.mainSlides, visible)
        if (this.thumbTrack) {
            this.syncTrack(this.thumbTrack, this.thumbSlides, visible)
        }
        this.desktopSlides.forEach((node, index) => {
            node.classList.toggle("hidden", !visible.has(index))
        })

        if (this.viewportNodeThumbCarousel) {
            this.viewportNodeThumbCarousel.style.display =
                visible.size >= 2 ? "" : "none"
        }

        if (reinit && this.emblaApiMain) {
            this.emblaApiMain.reInit()
            if (this.emblaApiThumb) this.emblaApiThumb.reInit()
            this.emblaApiMain.scrollTo(0, false)
        }
    }

    syncTrack(track, nodes, visible) {
        nodes.forEach((node, index) => {
            if (visible.has(index)) {
                // appendChild on an attached node moves it, so iterating in the
                // original order also restores the original order.
                track.appendChild(node)
            } else if (node.parentNode) {
                node.remove()
            }
        })
    }

    observeSelectedVariantAttribute() {
        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        if (!closestElement) return

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName ===
                        "data-product-card-selected_variant_id"
                ) {
                    const newValue = closestElement.getAttribute(
                        "data-product-card-selected_variant_id"
                    )
                    if (!newValue || !this.emblaApiMain) continue
                    this.applyVariantFilter(newValue)
                    this.scrollToSlide(newValue)
                }
            }
        })

        observer.observe(closestElement, { attributes: true })
    }

    handleInitialVariantSelection() {
        const initialVariantId = this.getSelectedVariantId()
        if (!initialVariantId) {
            console.warn(
                "No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection."
            )
            return
        }

        this.scrollToSlide(initialVariantId)
    }

    scrollToSlide(selectedVariantId) {
        const targetSlideIndex =
            this.findSlideIndexByVariantId(selectedVariantId)
        if (targetSlideIndex !== -1) {
            this.emblaApiMain.scrollTo(targetSlideIndex, false)
            if (this.emblaApiThumb) {
                this.emblaApiThumb.scrollTo(targetSlideIndex, false)
            }
        }
    }

    findSlideIndexByVariantId(variantId) {
        return [...this.emblaApiMain.slideNodes()].findIndex(
            (slide) => slide.dataset.targetVariant === variantId
        )
    }

    init() {
        this.emblaApiMain = EmblaCarousel(
            this.viewportNodeMainCarousel,
            this.options
        )

        if (this.viewportNodeThumbCarousel) {
            this.emblaApiThumb = EmblaCarousel(this.viewportNodeThumbCarousel, {
                ...this.options,
                ...this.option_thumbs,
            })
            const removeThumbHandlers = [
                this.addThumbBtnsClickHandlers(),
                this.addToggleThumbBtnsActive(),
            ]
            this.emblaApiThumb.on("destroy", () =>
                removeThumbHandlers.forEach((remove) => remove())
            )
        }

        const removeHandlers = [
            this.addPrevNextBtnsClickHandlers(
                this.emblaApiMain,
                this.elements.prevButton,
                this.elements.nextButton
            ),
            this.addProgressBarHandler(this.emblaApiMain),
        ]
        this.emblaApiMain.on("destroy", () =>
            removeHandlers.forEach((remove) => remove && remove())
        )
    }

    addProgressBarHandler(emblaApi) {
        const progressBar = this.elements.progressBar
        const progressContainer = this.elements.progressContainer
        if (!progressBar || !emblaApi) return null

        const updateProgress = () => {
            // Re-evaluated on every reInit: filtering can drop the gallery down
            // to a single slide, or bring it back up again.
            const scrollable =
                emblaApi.canScrollNext() || emblaApi.canScrollPrev()

            if (progressContainer) {
                progressContainer.style.display = scrollable ? "" : "none"
            }
            if (!scrollable) return

            // Embla's scrollProgress is 0 → 1 across the whole scrollable range.
            const progress = Math.max(
                0.1,
                Math.min(1, emblaApi.scrollProgress())
            )
            const percent = progress * 100
            progressBar.style.width = `${percent}%`
            if (progressContainer) {
                progressContainer.setAttribute(
                    "aria-valuenow",
                    Math.round(percent)
                )
            }
        }

        emblaApi
            .on("init", updateProgress)
            .on("reInit", updateProgress)
            .on("scroll", updateProgress)
            .on("select", updateProgress)

        // Run once in case "init" already fired before we subscribed.
        updateProgress()

        return () => {
            emblaApi
                .off("init", updateProgress)
                .off("reInit", updateProgress)
                .off("scroll", updateProgress)
                .off("select", updateProgress)
        }
    }

    addThumbBtnsClickHandlers() {
        // Bound to every thumb node once, including the ones currently filtered
        // out; the index is resolved at click time against the live slide list.
        const handlers = this.thumbSlides.map((slideNode) => {
            const handler = () => {
                const index = this.emblaApiThumb
                    .slideNodes()
                    .indexOf(slideNode)
                if (index !== -1) this.emblaApiMain.scrollTo(index, false)
            }
            slideNode.addEventListener("click", handler, false)
            return [slideNode, handler]
        })

        return () => {
            handlers.forEach(([slideNode, handler]) => {
                slideNode.removeEventListener("click", handler, false)
            })
        }
    }

    addToggleThumbBtnsActive() {
        const toggleThumbBtnsState = () => {
            const slidesThumbs = this.emblaApiThumb.slideNodes()
            const selected = this.emblaApiMain.selectedScrollSnap()
            this.emblaApiThumb.scrollTo(selected)
            slidesThumbs.forEach((slideNode, index) => {
                slideNode.classList.toggle("selected", index === selected)
            })
        }

        this.emblaApiMain
            .on("select", toggleThumbBtnsState)
            .on("reInit", toggleThumbBtnsState)
        this.emblaApiThumb
            .on("init", toggleThumbBtnsState)
            .on("reInit", toggleThumbBtnsState)

        return () => {
            this.thumbSlides.forEach((slide) =>
                slide.classList.remove("selected")
            )
        }
    }

    addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
        const scrollPrev = () => emblaApi.scrollPrev()
        const scrollNext = () => emblaApi.scrollNext()

        if (prevBtn) {
            prevBtn.addEventListener("click", scrollPrev, false)
        }

        if (nextBtn) {
            nextBtn.addEventListener("click", scrollNext, false)
        }

        const toggleState = () => {
            if (prevBtn) prevBtn.disabled = !emblaApi.canScrollPrev()
            if (nextBtn) nextBtn.disabled = !emblaApi.canScrollNext()
        }

        emblaApi
            .on("select", toggleState)
            .on("init", toggleState)
            .on("reInit", toggleState)

        return () => {
            if (prevBtn) prevBtn.removeEventListener("click", scrollPrev, false)
            if (nextBtn) nextBtn.removeEventListener("click", scrollNext, false)
        }
    }
}

customElements.define("product-media-gallery", ProductGallery)